import { Extension, Node as TiptapNode } from "@tiptap/core";
import {
  EditorContent,
  type Editor as TiptapEditor,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  UseEditorOptions,
  useEditor,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Plugin, PluginKey } from "prosemirror-state";
import {
  CSSProperties,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";

import { FieldReference } from "@/components/organisms/Field/FieldReferences";
import { RichTextEditorReferencePopper } from "@/components/organisms/RichTextEditor/RichTextEditorReferencePopper";
import { consumeCopiedReference } from "@/utils/clipboardReference";
import { typographyStyles } from "@/components/ui/typography";

export type TriggerKey = "@" | "#" | "/";

type ContextAttrs = {
  typename: string;
  id: string;
  identifier: string;
  name: string;
};

const ContextInline = TiptapNode.create({
  name: "contextInline",
  group: "inline",
  inline: true,
  atom: true,
  addAttributes() {
    return {
      typename: { default: "" },
      id: { default: "" },
      identifier: { default: "" },
      name: { default: "" },
    };
  },
  parseHTML() {
    return [{ tag: `span[data-${this.name}]` }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["span", { ...HTMLAttributes, [`data-${this.name}`]: "" }];
  },
});

function ContextNodeView(props: any) {
  const attrs: Partial<ContextAttrs> = props.node?.attrs || {};
  const hasName = !!attrs?.name;
  const onAdd = props?.options?.onContextAdded as
    | ((a: ContextAttrs) => void)
    | undefined;
  return (
    <NodeViewWrapper as="span">
      <RichTextEditorReferencePopper
        isOpenInitial={!hasName}
        onAdd={(option) => {
          const newAttrs: ContextAttrs = {
            typename: option.typename,
            id: option.id,
            identifier: option.identifier,
            name: option.name,
          };
          props.updateAttributes(newAttrs);
          onAdd?.(newAttrs);
          try {
            const basePos =
              typeof props.getPos === "function" ? props.getPos() : null;
            const insertAt =
              basePos != null
                ? basePos + (props.node?.nodeSize || 1)
                : undefined;
            if (insertAt != null && props.editor) {
              const state = props.editor.state as any;
              const $pos = state.doc.resolve(insertAt);
              const atEndOfParent =
                $pos.parentOffset === $pos.parent.content.size;
              props.editor.chain().setTextSelection(insertAt).run();
              if (atEndOfParent) {
                props.editor.chain().insertContent(" ").run();
              }
            } else if (props.editor) {
              // Fallback: only add space if selection is at end
              const state = props.editor.state as any;
              const sel: any = state.selection;
              const $from = sel.$from;
              const atEnd = $from.parentOffset === $from.parent.content.size;
              if (atEnd) props.editor.chain().focus().insertContent(" ").run();
            }
          } catch (e) {
            // noop
          }
        }}
      >
        {hasName ? (
          <FieldReference
            value={{
              id: attrs.id || "",
              name: attrs.name || "",
              identifier: attrs.identifier || "",
              __typename: attrs.typename || "",
            }}
            valueOptions={{ style: "text", isNullable: true }}
            events={{ onDeleteField: () => {} }}
            isMultiline={false}
            style={{
              paddingLeft: 0,
              paddingRight: 0,
              display: "inline",
            }}
          />
        ) : (
          <span style={{ display: "inline-block" }}>@</span>
        )}
      </RichTextEditorReferencePopper>
    </NodeViewWrapper>
  );
}

const triggerRouterPluginKey = new PluginKey("triggerRouter");

function createTriggerRouter(triggers: TriggerKey[]) {
  return Extension.create({
    name: "triggerRouter",
    addProseMirrorPlugins() {
      const editor = this.editor;
      return [
        new Plugin({
          key: triggerRouterPluginKey,
          props: {
            handleTextInput(view, from, _to, text) {
              // Only handle @ character
              if (!triggers.includes("@") || text !== "@") {
                return false;
              }

              // Get character before cursor
              const charBefore =
                from > 1 ? view.state.doc.textBetween(from - 1, from) : "";

              // If preceded by non-whitespace, insert literal @ (for emails)
              if (charBefore && !/\s/.test(charBefore)) {
                return false; // Let default handling insert the @
              }

              // Trigger reference picker
              editor
                .chain()
                .focus()
                .insertContent({ type: "contextInline", attrs: { name: "" } })
                .run();
              return true;
            },
          },
        }),
      ];
    },
  });
}

export type RichTextInputRef = {
  getSerializedText: () => string;
  getSerializedArray: () => Array<{ text?: string; id?: string }>;
  clear: () => void;
  focusEnd: () => void;
  setDraft: (text: string) => boolean;
};

function safelyFocusEditor(
  editor: TiptapEditor | null | undefined,
  position: "start" | "end" = "end",
) {
  if (!editor) return;

  try {
    const editorView = editor.view;
    if (!editorView?.dom?.isConnected) {
      return;
    }

    editor.commands.focus(position);
  } catch {
    // Ignore focus attempts while the editor is mounting/unmounting.
  }
}

export function applyPlainTextDraft(editor: TiptapEditor, text: string): void {
  editor.commands.setContent({
    type: "doc",
    content: text
      ? [
          {
            type: "paragraph",
            content: [{ type: "text", text }],
          },
        ]
      : [],
  });
  safelyFocusEditor(editor, "end");
}

export function serializeTipTapJSON(node: any): string {
  if (!node) return "";
  if (node.type === "text") return node.text || "";
  const childrenText = (node.content || []).map(serializeTipTapJSON).join("");
  if (node.type === "paragraph") return childrenText;
  if (node.type === "contextInline") {
    const a = node.attrs || {};
    return `@{${a.name || ""}:${a.id || ""}}`;
  }
  return childrenText;
}

export function serializeTipTapJSONAsArray(
  node: any,
): Array<{ text?: string; id?: string }> {
  if (!node) return [];

  if (node.type === "text") {
    const text = node.text || "";
    return text ? [{ text }] : [];
  }

  if (node.type === "contextInline") {
    const a = node.attrs || {};
    return [{ id: a.id || "" }];
  }

  if (node.type === "paragraph") {
    const result: Array<{ text?: string; id?: string }> = [];
    const content = node.content || [];

    for (const child of content) {
      const childResult = serializeTipTapJSONAsArray(child);
      result.push(...childResult);
    }

    return result;
  }

  // For other node types, process children
  const result: Array<{ text?: string; id?: string }> = [];
  const content = node.content || [];

  for (const child of content) {
    const childResult = serializeTipTapJSONAsArray(child);
    result.push(...childResult);
  }

  return result;
}

export type RichTextInputProps = {
  triggers?: TriggerKey[];
  enableContextInline?: boolean;
  blockEnter?: boolean;
  onEnter?: () => void;
  onMetaEnter?: () => void;
  onEscape?: () => void;
  onUpdateText?: (text: string) => void;
  onContextAdded?: (attrs: ContextAttrs) => void;
  onContextDeleted?: (id: string) => void;
  placeholder?: string;
  style?: React.CSSProperties;
  autoFocus?: boolean;
  onEditorCreate?: (editor: TiptapEditor) => void;
};

export const RichTextInput = forwardRef<RichTextInputRef, RichTextInputProps>(
  (
    {
      triggers = ["@"],
      enableContextInline = true,
      blockEnter = false,
      onEnter,
      onMetaEnter,
      onEscape,
      onUpdateText,
      onContextAdded,
      onContextDeleted,
      placeholder,
      style,
      autoFocus = true,
      onEditorCreate,
    },
    ref,
  ) => {
    // Use refs to avoid stale closures in TipTap's handleKeyDown and extensions
    const onEnterRef = useRef(onEnter);
    const onMetaEnterRef = useRef(onMetaEnter);
    const onEscapeRef = useRef(onEscape);
    const onContextDeletedRef = useRef(onContextDeleted);
    const onContextAddedRef = useRef(onContextAdded);
    const onUpdateTextRef = useRef(onUpdateText);
    const autoFocusRef = useRef(autoFocus);
    const enableContextInlineRef = useRef(enableContextInline);
    const onEditorCreateRef = useRef(onEditorCreate);
    onEnterRef.current = onEnter;
    onMetaEnterRef.current = onMetaEnter;
    onEscapeRef.current = onEscape;
    onContextDeletedRef.current = onContextDeleted;
    onContextAddedRef.current = onContextAdded;
    onUpdateTextRef.current = onUpdateText;
    autoFocusRef.current = autoFocus;
    enableContextInlineRef.current = enableContextInline;
    onEditorCreateRef.current = onEditorCreate;

    // Memoize extensions so the same instances are used across re-renders.
    // New instances each render cause ProseMirror "Adding different instances of a keyed plugin" when the editor reconfigures.
    const triggersKey = triggers?.join(",") ?? "@";
    const extensions = useMemo(() => {
      const contextExtension = ContextInline.extend({
        addNodeView: () => ReactNodeViewRenderer(ContextNodeView as any),
        addOptions() {
          return {
            get onContextAdded() {
              return onContextAddedRef.current;
            },
          };
        },
      });
      return [
        StarterKit.configure({
          paragraph: { HTMLAttributes: { style: "margin:0;padding:0" } },
        }),
        ...(enableContextInline ? [contextExtension] : []),
        createTriggerRouter(triggers),
      ];
    }, [enableContextInline, triggersKey]);

    // Stable editorProps so useEditor's compareOptions sees no change and never calls setOptions().
    // setOptions() triggers a reconfigure which re-runs addProseMirrorPlugins() and creates new Plugin instances → keyed plugin error.
    const editorPropsStable = useMemo(
      () => ({
        attributes: {
          style:
            "margin:0;padding:0;border:0;border-radius:0;cursor:text;outline:none;white-space:pre-wrap;word-break:break-word;",
        },
        handlePaste: (view: any, event: ClipboardEvent) => {
          if (!enableContextInlineRef.current) return false;
          const clipboardText = event.clipboardData
            ?.getData("text/plain")
            ?.trim();
          if (!clipboardText) return false;
          const ref = consumeCopiedReference();
          if (!ref || ref.identifier !== clipboardText) return false;

          event.preventDefault();
          const nodeType = view.state.schema.nodes.contextInline;
          if (!nodeType) return false;
          const node = nodeType.create({
            typename: ref.typename,
            id: ref.id,
            identifier: ref.identifier,
            name: ref.name,
          });
          const tr = view.state.tr.replaceSelectionWith(node);
          view.dispatch(tr);
          return true;
        },
        handleKeyDown: (view: unknown, event: Event) => {
          const key = (event as KeyboardEvent).key;
          const e = event as KeyboardEvent;

          if (key === "Enter" && e.shiftKey) return false;
          if (key === "Enter" && (e.metaKey || e.ctrlKey)) {
            event.preventDefault();
            onMetaEnterRef.current?.();
            return true;
          }
          if (key === "Enter") {
            event.preventDefault();
            (onEnterRef.current ?? onMetaEnterRef.current)?.();
            return true;
          }
          if (key === "Escape" && onEscapeRef.current) {
            event.preventDefault();
            onEscapeRef.current();
            return true;
          }
          if (key === "Backspace" && enableContextInlineRef.current) {
            const v = view as {
              state: {
                selection: {
                  $from: {
                    pos: number;
                    nodeBefore: {
                      type: { name: string };
                      nodeSize: number;
                      attrs?: { id?: string };
                    } | null;
                  };
                };
                tr: { delete: (a: number, b: number) => unknown };
              };
              dispatch: (tr: unknown) => void;
            };
            const sel = v.state.selection;
            const before = sel.$from.nodeBefore;
            if (before?.type?.name === "contextInline") {
              const pos = sel.$from.pos - before.nodeSize;
              const tr = v.state.tr.delete(pos, pos + before.nodeSize);
              v.dispatch(tr);
              const deletedId = before.attrs?.id;
              if (deletedId) onContextDeletedRef.current?.(deletedId);
              return true;
            }
          }
          return false;
        },
      }),
      [],
    );

    const onCreateStable = useCallback(
      ({ editor }: { editor: TiptapEditor }) => {
        onEditorCreateRef.current?.(editor);
        if (autoFocusRef.current) {
          setTimeout(() => safelyFocusEditor(editor, "end"), 0);
        }
      },
      [],
    );

    const onUpdateStable = useCallback(
      ({ editor }: { editor: { getJSON: () => unknown } }) => {
        onUpdateTextRef.current?.(serializeTipTapJSON(editor.getJSON()));
      },
      [],
    );

    const editorOptions = useMemo(
      () => ({
        immediatelyRender: false,
        extensions,
        onCreate: onCreateStable,
        onUpdate: onUpdateStable,
        editorProps: editorPropsStable,
        content: "",
      }),
      [extensions, onCreateStable, onUpdateStable, editorPropsStable],
    );

    const editor = useEditor(editorOptions as UseEditorOptions, []);
    useImperativeHandle(
      ref,
      () => ({
        getSerializedText: () => serializeTipTapJSON(editor?.getJSON()),
        getSerializedArray: () => serializeTipTapJSONAsArray(editor?.getJSON()),
        clear: () => editor?.commands.setContent("<p></p>"),
        focusEnd: () => safelyFocusEditor(editor, "end"),
        setDraft: (text: string) => {
          if (!editor) return false;
          applyPlainTextDraft(editor, text);
          return true;
        },
      }),
      [editor],
    );
    return (
      <div onClick={() => safelyFocusEditor(editor, "end")} className="w-full">
        <EditorContent
          editor={editor}
          placeholder={placeholder}
          style={{
            width: "100%",
            margin: 0,
            padding: 0,
            textAlign: "left",
            outline: "none",
            ...(typographyStyles["body-md"] as CSSProperties),
            ...style,
          }}
        />
      </div>
    );
  },
);

RichTextInput.displayName = "RichTextInput";
